import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { setSimplifiedActive } from "@/lib/simplifiedCopy";
import {
  applyTheme,
  applyDecoration,
  applyFontScale,
  applyVisionMode,
  DEFAULT_THEME,
  isThemeId,
  readStoredTheme,
  THEMES,
  ThemeId,
  writeStoredTheme,
  applyAccent,
  DEFAULT_PREFS,
  normalizePrefs,
  readStoredPrefs,
  writeStoredPrefs,
  ThemePrefs,
} from "@/lib/themes";

// ─────────────────────────────────────────────────────────────────────────────
// ThemeProvider — owns the active theme. Resolution priority:
//   1. profiles.theme of the signed-in user (server source of truth)
//   2. localStorage (so anonymous browsing remembers your choice)
//   3. DEFAULT_THEME
// Calling setTheme() writes to all three, so the change is instant in the UI
// AND survives reloads / new devices.
// ─────────────────────────────────────────────────────────────────────────────

interface ThemeContextValue {
  theme: ThemeId;
  setTheme: (id: ThemeId) => Promise<void>;
  themes: typeof THEMES;
  prefs: ThemePrefs;
  setPrefs: (next: Partial<ThemePrefs>) => Promise<void>;
  resetPrefs: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<ThemeId>(() => readStoredTheme());
  const [prefs, setPrefsState] = useState<ThemePrefs>(() => readStoredPrefs());

  // Apply on mount + whenever theme changes.
  useEffect(() => { applyTheme(theme); }, [theme]);

  // Paper grain intensity comes straight from prefs.paperTexture and writes
  // `data-decoration` on the root so index.css can switch the noise overlay.
  useEffect(() => {
    applyDecoration(prefs.paperTexture);
  }, [prefs.paperTexture]);

  // Root font-size scaling — applied to <html> so rem-based UI scales together.
  useEffect(() => {
    applyFontScale(prefs.fontScale);
  }, [prefs.fontScale]);

  // High-contrast accessibility overlay — applied on top of the active theme.
  // Re-runs after applyTheme so the override always wins.
  useEffect(() => {
    applyVisionMode(prefs.visionMode, theme);
  }, [prefs.visionMode, theme]);

  // Re-apply accent any time the theme changes (theme writes --rust first,
  // and we want our override to win) or when accent changes directly.
  useEffect(() => { applyAccent(prefs.accent); }, [theme, prefs.accent]);

  // Keep the module-level reading-style mirror (used by sonner toast wrapper
  // and other non-React callers) in sync with the active preference.
  useEffect(() => {
    setSimplifiedActive(prefs.readingStyle === "simplified");
  }, [prefs.readingStyle]);

  // When the user signs in, prefer their stored theme. We do NOT downgrade
  // anonymous selections — if they just picked a theme on the splash page
  // we keep showing it until the profile load completes.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("theme, theme_prefs")
        .eq("id", user.id)
        .maybeSingle();
      if (cancelled) return;
      const row = data as { theme?: string; theme_prefs?: unknown } | null;
      const remote = row?.theme;
      if (isThemeId(remote) && remote !== theme) {
        setThemeState(remote);
        writeStoredTheme(remote);
      }
      if (row?.theme_prefs) {
        const remotePrefs = normalizePrefs(row.theme_prefs);
        setPrefsState(remotePrefs);
        writeStoredPrefs(remotePrefs);
      }
    })();
    return () => { cancelled = true; };
    // theme is intentionally omitted — we only sync on user identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const setTheme = useCallback(async (id: ThemeId) => {
    setThemeState(id);
    writeStoredTheme(id);
    if (user) {
      // Cast through unknown so a stale generated types file doesn't fail
      // the build before the column propagates.
      await supabase
        .from("profiles")
        .update({ theme: id, theme_prefs: prefs } as unknown as Record<string, never>)
        .eq("id", user.id);
    }
  }, [user, prefs]);

  const setPrefs = useCallback(async (next: Partial<ThemePrefs>) => {
    const merged: ThemePrefs = { ...prefs, ...next };
    setPrefsState(merged);
    writeStoredPrefs(merged);
    if (user) {
      // Mirror the two flat columns so other surfaces (admin, exports)
      // can read them without unpacking theme_prefs JSON.
      const flat: Record<string, unknown> = { theme_prefs: merged };
      if (next.visionMode !== undefined) flat.vision_mode = merged.visionMode;
      if (next.readingStyle !== undefined) flat.reading_style = merged.readingStyle;
      await supabase
        .from("profiles")
        .update(flat as unknown as Record<string, never>)
        .eq("id", user.id);
    }
  }, [prefs, user]);

  const resetPrefs = useCallback(async () => {
    setPrefsState({ ...DEFAULT_PREFS });
    writeStoredPrefs({ ...DEFAULT_PREFS });
    if (user) {
      await supabase
        .from("profiles")
        .update({ theme_prefs: DEFAULT_PREFS } as unknown as Record<string, never>)
        .eq("id", user.id);
    }
  }, [user]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: THEMES, prefs, setPrefs, resetPrefs }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Fail soft — return a non-mutating shim. This matters because some
    // routes (Index, splash) render before the provider mounts in tests.
    return {
      theme: DEFAULT_THEME,
      setTheme: async () => { /* noop */ },
      themes: THEMES,
      prefs: { ...DEFAULT_PREFS },
      setPrefs: async () => { /* noop */ },
      resetPrefs: async () => { /* noop */ },
    } as ThemeContextValue;
  }
  return ctx;
}